import * as React from "react"
import { Info } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useBookingT } from "@/hooks/use-translations"
import { fetchHrisAccess, type HrisAccessReason } from "@/features/integrations/hris/hrisApi"

type Props = {
  providerName: string
  children: React.ReactNode
}

/**
 * Shows the HR-system panel only to who may connect it — the company's
 * director or TMC admin, on a plan with HR integrations — and tells everyone
 * else what to do instead of a bare "Access denied".
 */
export function HrisAccessGate({ providerName, children }: Props) {
  const t = useBookingT()
  const [access, setAccess] = React.useState<{ allowed: boolean; reason: HrisAccessReason | null } | null>(null)

  React.useEffect(() => {
    let cancelled = false
    fetchHrisAccess()
      .then((res) => !cancelled && setAccess(res))
      .catch(() => !cancelled && setAccess({ allowed: false, reason: "role" }))
    return () => {
      cancelled = true
    }
  }, [])

  if (access === null) return <Skeleton className="h-40 rounded-lg" />
  if (access.allowed) return <>{children}</>

  const fill = (text: string) => text.split("{provider}").join(providerName)
  const copy: Record<HrisAccessReason, { title: string; text: string }> = {
    staff: {
      title: t("hris_access_staff_title", "Platform administrators do not connect an HR system to their own account"),
      text: fill(
        t(
          "hris_access_staff_text",
          "{provider} is connected by the company's director: they accept Schedule E and allow Travel Code to receive the company's employee data. Log in as that director (“Log in as”) and open this page, or ask the director to connect it.",
        ),
      ),
    },
    role: {
      title: t("hris_access_role_title", "Only the company's director can connect the HR system"),
      text: fill(
        t(
          "hris_access_role_text",
          "Connecting {provider} accepts Schedule E and shares the company's employee data with Travel Code, so it is done by the company's director or TMC administrator. Ask them to open Integrations › {provider}.",
        ),
      ),
    },
    plan: {
      title: t("hris_access_plan_title", "Your plan does not include HR integrations"),
      text: fill(t("hris_access_plan_text", "To connect {provider}, move to a plan with HR integrations in Plan & billing, or contact us.")),
    },
    rollout: {
      title: t("hris_access_rollout_title", "Not available for your company yet"),
      text: fill(t("hris_access_rollout_text", "We are opening {provider} to companies step by step. Use “Request integration” above and we will let you know.")),
    },
  }
  const message = copy[access.reason ?? "role"]

  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-6">
        <Info className="mt-0.5 size-5 shrink-0 text-brand-500" />
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-neutral-900">{message.title}</h2>
          <p className="max-w-2xl text-sm text-neutral-800">{message.text}</p>
          {access.reason === "plan" && (
            <a href="/app/subscription" className="inline-block pt-1 text-sm font-medium text-brand-600 hover:underline">
              {t("hris_access_plan_link", "Open Plan & billing")}
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
